class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.isLandingPage = /(?:^|\/)pages(?:\/|$)/.test(window.location.pathname);
    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    // Delegation survives replacement of the drawer contents.
    this.addEventListener('click', (event) => {
      if (event.target.matches('#CartDrawer-Overlay')) this.close();
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    if (this.isLandingPage) return;
    const selector = '#cart-icon-bubble, .shield-nav__cart a, .shield-pill__cart a';
    document.querySelectorAll(selector).forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.setAttribute('aria-controls', 'CartDrawer');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code === 'Space') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble) {
      this.iconObserver = new MutationObserver(() => this.syncVisibleCartIcons());
      this.iconObserver.observe(bubble, { childList: true, subtree: true, characterData: true });
      this.syncVisibleCartIcons();
    }
  }

  syncVisibleCartIcons() {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble || this.isLandingPage) return;
    document.querySelectorAll('.shield-nav__cart a, .shield-pill__cart a').forEach((link) => {
      link.innerHTML = bubble.innerHTML;
    });
  }

  open(triggeredBy) {
    if (this.isLandingPage) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    clearTimeout(this.openTimer);
    if (this.focusOnTransition) this.removeEventListener('transitionend', this.focusOnTransition);
    this.openTimer = setTimeout(() => {
      this.classList.add('animate', 'active');
      const inner = this.querySelector('.drawer__inner');
      const focusDrawer = () => {
        if (inner?.isConnected && this.classList.contains('active')) trapFocus(inner, inner);
      };
      // Initial opens can remain unfocusable during the visibility transition.
      // Already-open rerenders and reduced-motion views need no transition event.
      this.focusOnTransition = focusDrawer;
      this.addEventListener('transitionend', focusDrawer, { once: true });
      requestAnimationFrame(focusDrawer);
    });
    document.body.classList.add('overflow-hidden');
  }

  close() {
    clearTimeout(this.openTimer);
    if (this.focusOnTransition) this.removeEventListener('transitionend', this.focusOnTransition);
    this.classList.remove('active');
    const trigger = this.activeElement?.isConnected ? this.activeElement :
      Array.from(document.querySelectorAll('.shield-nav__cart a, .shield-pill__cart a'))
        .find((link) => link.getClientRects().length);
    removeTrapFocus(trigger);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(summary) {
    summary.setAttribute('role', 'button');
    summary.setAttribute('aria-expanded', String(summary.parentElement.hasAttribute('open')));
    if (summary.nextElementSibling?.id) summary.setAttribute('aria-controls', summary.nextElementSibling.id);
    summary.addEventListener('click', () => {
      summary.setAttribute('aria-expanded', String(!summary.parentElement.hasAttribute('open')));
    });
    summary.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  applySections(sections, { open = false } = {}) {
    if (!sections || typeof sections['cart-drawer'] !== 'string' ||
        typeof sections['cart-icon-bubble'] !== 'string') return false;
    const newDrawer = this.getSectionDOM(sections['cart-drawer'], 'cart-drawer');
    const newContents = newDrawer?.querySelector('#CartDrawer');
    const newBubble = this.getSectionDOM(sections['cart-icon-bubble']);
    const contents = this.querySelector('#CartDrawer');
    const bubble = document.getElementById('cart-icon-bubble');
    if (!newContents || !newBubble || !contents || !bubble) return false;
    const wasOpen = this.classList.contains('active');
    contents.innerHTML = newContents.innerHTML;
    this.classList.toggle('is-empty', newDrawer.classList.contains('is-empty'));
    bubble.innerHTML = newBubble.innerHTML;
    this.syncVisibleCartIcons();
    if (open || wasOpen) this.open();
    return true;
  }

  async renderContents(parsedState) {
    this.productId = parsedState.id;
    if (this.applySections(parsedState.sections, { open: true })) return true;
    // A successful add can return null sections. Fetch HTML; never repeat the add.
    return this.refreshContents({ open: true });
  }

  async refreshContents({ open = false, origin = null } = {}) {
    try {
      const url = new URL(window.routes?.cart_url || '/cart', window.location.origin);
      url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
      const response = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Cart refresh failed');
      const sections = await response.json();
      // Do not apply an older update after its originating cart element was replaced.
      if (origin && !origin.isConnected) return false;
      if (!this.applySections(sections, { open })) throw new Error('Cart sections unavailable');
      return true;
    } catch (error) {
      const errors = this.querySelector('#CartDrawer-CartErrors');
      if (errors) {
        errors.textContent = 'The cart display could not refresh. Open your cart to check its latest contents. ';
        const link = document.createElement('a');
        link.href = window.routes?.cart_url || '/cart';
        link.textContent = 'View cart';
        errors.append(link);
      }
      if (open) this.open();
      return false;
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return this.getSectionDOM(html, selector)?.innerHTML || '';
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // These paths already render fresh drawer sections themselves.
      if (['product-form', 'shield-cart-upsell'].includes(event.source)) return;
      if (event.source === 'cart-items' && !document.querySelector('cart-items')) return;
      this.onCartUpdate();
    });
  }

  onCartUpdate() {
    return this.closest('cart-drawer')?.refreshContents({ origin: this });
  }

  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

(() => {
  let activeButton;
  let originalHTML;
  let resetTimer;

  function resetCheckoutButton() {
    clearTimeout(resetTimer);
    if (!activeButton) return;
    activeButton.innerHTML = originalHTML;
    activeButton.classList.remove('sd-checkout-loading');
    activeButton.removeAttribute('aria-busy');
    activeButton.removeAttribute('aria-disabled');
    activeButton = null;
  }

  // Delegation also works after the drawer's contents are refreshed.
  document.addEventListener('submit', (event) => {
    const button = event.submitter;
    if (event.defaultPrevented || button?.id !== 'CartDrawer-Checkout') return;

    if (activeButton === button) {
      event.preventDefault();
      return;
    }

    resetCheckoutButton();
    activeButton = button;
    originalHTML = button.innerHTML;
    button.classList.add('sd-checkout-loading');
    button.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-disabled', 'true');
    button.innerHTML =
      '<span class="sd-checkout-spinner" aria-hidden="true"></span>' +
      '<span>Loading checkout\u2026</span>';

    // Keep the named checkout button enabled for the native form submission.
    // Allow a retry if navigation is blocked or takes unusually long.
    resetTimer = setTimeout(resetCheckoutButton, 20000);
  });

  // Restore the button when returning from checkout with the Back button.
  window.addEventListener('pageshow', resetCheckoutButton);
})();